import React, { useCallback } from 'react'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  Dialog,
  Typography,
  useInput,
  useViewport,
  DatePicker,
  useDatePicker,
  formatDate
} from '@chilecompra/react-kit'
import { GridContainer, GridItem, RootContainer, ExclamationIcon, Button } from './EditAutoParticipantModal.styles'
import { useDispatch, useSelector } from 'react-redux'
import {
  onSetAutoEditModalShowSuccess,
  onSendAutoParticipantDataEditedThunk,
  onSetAutoEditStartDateSuccess,
  onSetAutoEditEndDateSuccess
} from './EditAutoParticipantModal.actions'
import { PUBLIC_URL } from '../../config/settings/environment'
import {
  checkInputNationalDniError,
  checkInputMaxDateTyping,
  checkInputMinDateTyping
} from '../../modules/utils/checkers'
import { onGetParticipantsListThunk } from '../ListParticipants/ListParticipants.actions'
import { useParams } from 'react-router-dom'
import { PATTERN_DATE_OUT, PATTERN_TIME_START_OUT, PATTERN_TIME_END_OUT } from '../../config/settings/constants'

/**
 * The Edit Participant (Auto) modal container.
 */
const EditAutoParticipantModalContainer = () => {
  const { processId, fromWhere } = useParams()
  const dispatch = useDispatch()
  const {
    size: { isTiny, isSmall, isDownToMedium }
  } = useViewport()

  const {
    loading,
    showEditAutoParticipantModal,
    fullName,
    role,
    dni,
    email,
    isForeignPerson,
    functionId: functionIdSelected,
    processId: processIdSelected,
    participantId: selectedParticipantId,
    participantDetailId,
    originalProcessId,
    originalFunctionId,
    startDate,
    endDate,
    isManual,
    processName,
    functionName,
    participantProcessId
  } = useSelector(state => state.editAutoParticipantModal)

  const handleCloseModal = () => {
    dispatch(onSetAutoEditModalShowSuccess({ showEditAutoParticipantModal: false }))
  }

  const { value: fullNameValue, error: errorFullNameValue } = useInput({
    initialValue: fullName || '',
    errorCallbacks: [checkInputErrorRequired('Debes ingresar el nombre completo.')],
    overwriteCallback: () => fullName
  })

  const { value: roleValue, error: errorRoleValue } = useInput({
    initialValue: role || '',
    errorCallbacks: [checkInputErrorRequired('Debes ingresar el nombre completo.')],
    overwriteCallback: () => role
  })

  const { value: dniValue, error: errorDniValue } = useInput({
    initialValue: dni || '',
    errorCallbacks: isForeignPerson
      ? [checkInputErrorRequired('Debes ingresar un RUT')]
      : [checkInputErrorRequired('Debes ingresar un RUT'), checkInputNationalDniError],
    overwriteCallback: () => dni
  })

  const { value: emailValue, error: errorEmailValue } = useInput({
    initialValue: email || '',
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    overwriteCallback: () => email
  })

  const {
    error: errorDateStart,
    value: dateStartValue,
    onChange: handleDateStartChange
  } = useDatePicker({
    overwriteCallback: () => startDate,
    initialValue: startDate || '',
    errorCallbacks: !endDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha inicio de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha inicio de función'),
          checkInputMaxDateTyping(new Date(endDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetAutoEditStartDateSuccess({
          startDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_START_OUT}`).replace(
            ' ',
            'T'
          )
        })
      )
  })

  const {
    error: errorDateEnd,
    value: dateEndValue,
    onChange: handleDateEndChange
  } = useDatePicker({
    overwriteCallback: () => endDate,
    initialValue: endDate || '',
    errorCallbacks: !startDate
      ? [checkInputErrorRequired('Debes seleccionar una fecha fin de función')]
      : [
          checkInputErrorRequired('Debes seleccionar una fecha fin de función'),
          checkInputMinDateTyping(new Date(startDate))
        ],
    changeCallback: updatedValue =>
      dispatch(
        onSetAutoEditEndDateSuccess({
          endDate: formatDate(new Date(updatedValue), `${PATTERN_DATE_OUT} ${PATTERN_TIME_END_OUT}`).replace(' ', 'T')
        })
      )
  })

  const handleCallbackExcecute = () => {
    const payload = {
      processNumber: processId,
      fromWhere
    }
    dispatch(onGetParticipantsListThunk(payload))
    dispatch(
      onSetAutoEditModalShowSuccess({
        showEditManuaParticipantModal: false,
        participantSelected: '',
        fullName: '',
        role: '',
        dni: '',
        email: '',
        isForeignPerson: '',
        participantId: '',
        participantDetailId: '',
        functionId: '',
        processId: '',
        originalProcessId: '',
        originalFunctionId: '',
        startDate: '',
        endDate: '',
        isManual: null,
        processName: '',
        functionName: '',
        participantProcessId: ''
      })
    )
  }

  const handleSubmit = () => {
    const selectedProcess = { name: functionName, value: functionIdSelected }
    const selectedFunction = { name: processName, value: processIdSelected }
    const payload = {
      fullNameValue,
      roleValue,
      dniValue,
      emailValue,
      selectedProcess,
      selectedFunction,
      participantId: selectedParticipantId,
      participantDetailId,
      onSuccessCallback: handleCallbackExcecute,
      isForeignPerson,
      functionProcessChanged: originalProcessId !== processIdSelected || originalFunctionId !== functionIdSelected,
      startDate,
      endDate,
      isManual,
      participantProcessId
    }
    dispatch(onSendAutoParticipantDataEditedThunk(payload))
  }

  const canNotSubmit = useCallback(() => {
    const haveSomeThing =
      dniValue !== '' &&
      fullNameValue !== '' &&
      emailValue !== '' &&
      roleValue !== '' &&
      processIdSelected !== '' &&
      dateStartValue !== '' &&
      dateEndValue !== '' &&
      functionIdSelected !== ''
    const haveError =
      errorDateStart !== '' ||
      errorDateEnd !== '' ||
      errorDniValue !== '' ||
      errorFullNameValue !== '' ||
      errorEmailValue !== '' ||
      errorRoleValue !== ''
    return !haveSomeThing || haveError
  }, [
    dateStartValue,
    dateEndValue,
    dniValue,
    emailValue,
    fullNameValue,
    roleValue,
    processIdSelected,
    functionIdSelected,
    errorDateEnd,
    errorDateStart
  ])

  const disabledSubmit = canNotSubmit()

  return (
    <Dialog
      open={showEditAutoParticipantModal}
      onClose={handleCloseModal}
      margin="0 20px"
      padding="0"
      maxWidth="750px"
      disableBackdropClick
    >
      <RootContainer justifyContent="center">
        <GridItem xs={12} container justifyContent="center">
          <ExclamationIcon src={`${PUBLIC_URL}/assets/images/alert_icon.svg`} />
        </GridItem>
        <GridItem xs={12} padding="37px 0 0">
          <Typography fontSize="18px" color="black1" fontWeight="bold" variant="h3" align="center">
            Editar fechas de participación
          </Typography>
        </GridItem>
        <GridItem xs={12} md={8} padding="27px 0 0">
          <Typography
            fontSize={isDownToMedium ? '14px' : '16px'}
            color="black1"
            fontWeight="regular"
            variant="h3"
            align={isDownToMedium ? 'left' : 'center'}
          >
            Puedes realizar edición de información en el caso de participantes externos a Mercado Público.
          </Typography>
        </GridItem>
        <GridItem container item xs={12} md={10} justifyContent="center">
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={isDownToMedium ? '20px 0 0' : '40px 0 10px'}
          >
            <GridItem xs={12} padding="0">
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                Participante
              </Typography>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {fullNameValue}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={isDownToMedium ? '10px 0 0' : '40px 0 10px'}
          >
            <GridItem xs={12}>
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                Cargo
              </Typography>
            </GridItem>
            <GridItem xs={12}>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {roleValue}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={isDownToMedium ? '20px 0 0' : '5px 0 10px'}>
            <GridItem xs={12} padding="0">
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                RUT
              </Typography>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {dniValue}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={isDownToMedium ? '20px 0 0' : '5px 0 10px'}>
            <GridItem xs={12} padding="0">
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                Correo electrónico
              </Typography>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {emailValue}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={isDownToMedium ? '10px 0 0' : '5px 0 10px'}>
            <GridItem xs={12}>
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                Etapa del proceso
              </Typography>
            </GridItem>
            <GridItem xs={12}>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {processName}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={isDownToMedium ? '10px 0 0' : '5px 0 10px'}>
            <GridItem xs={12}>
              <Typography fontSize="14px" color="black1" fontWeight="bold" variant="h3" align="left">
                Función
              </Typography>
            </GridItem>
            <GridItem xs={12}>
              <Typography fontSize="16px" color="black1" fontWeight="regular" variant="h3" align="left">
                {functionName}
              </Typography>
            </GridItem>
          </GridItem>
          <GridItem
            xs={12}
            md={6}
            container
            justifyContent="left"
            padding={!isTiny && !isSmall ? '10px 20px 10px 0' : '0'}
          >
            <DatePicker
              name="dateStartValue"
              error={errorDateStart}
              label="Fecha inicio de función"
              value={dateStartValue}
              maxDate={dateEndValue}
              onChange={handleDateStartChange}
              width="100%"
            />
          </GridItem>
          <GridItem xs={12} md={6} container justifyContent="left" padding={!isTiny && !isSmall ? '10px 0' : '0'}>
            <DatePicker
              name="dateEndValue"
              error={errorDateEnd}
              label="Fecha fin de función"
              value={dateEndValue}
              minDate={dateStartValue}
              onChange={handleDateEndChange}
              width="100%"
            />
          </GridItem>
        </GridItem>
        <GridItem xs={12} padding="24px 0 0">
          <GridContainer justifyContent="center">
            <GridItem xs={12} container justifyContent="center">
              <Button disabled={loading || disabledSubmit} loading={loading} onClick={handleSubmit} color="primary">
                Guardar
              </Button>
            </GridItem>
            <GridItem xs={12} container justifyContent="center" padding="10px 0">
              <Button onClick={handleCloseModal} color="default" variant="text">
                Cancelar
              </Button>
            </GridItem>
          </GridContainer>
        </GridItem>
      </RootContainer>
    </Dialog>
  )
}

export default EditAutoParticipantModalContainer
